import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus,
  FolderKanban,
  Archive,
  Search,
  Lock,
  Star,
  X,
  AlertCircle,
  AtSign,
  Settings2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, PageHeader, Skeleton } from '@solid/ui';
import { projectsApi, organizationsApi } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Avatar } from '@/components/ui/Avatar';
import { ProjectIcon, projectOrgName } from '@/components/project/ProjectIcon';
import { AdminActionsMenu, createEditAction, createArchiveAction } from '@/components/ui/AdminActionsMenu';
import { cn, formatRelativeTime } from '@/lib/utils';
import { CreateProjectModal } from '@/components/modals/CreateProjectModal';
import { EditProjectModal } from '@/components/modals/EditProjectModal';
import { useAuthStore, getCanCreateProjects, getIsProjectAdmin } from '@/stores/auth.store';
import { useCanFavorite } from '@/hooks/useCanFavorite';

type FilterTab = 'active' | 'archived';
type SortKey = 'activity' | 'work' | 'name' | 'organization';

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'activity', label: 'Recent activity' },
  { value: 'work', label: 'My open work' },
  { value: 'name', label: 'Name' },
  { value: 'organization', label: 'Organization' },
];

/** What the projects list says about a project. */
interface Signals {
  mine: { open: number; dueSoon: number; overdue: number };
  waitingOn: number;
  progress: { done: number; total: number };
  nextDue: string | null;
  unseen: { unread: number; mentions: number };
  lastActor: { id: string; name: string | null; avatarUrl: string | null } | null;
}

function signalsOf(project: any): Signals {
  return {
    mine: project.mine ?? { open: 0, dueSoon: 0, overdue: 0 },
    waitingOn: project.waitingOn ?? 0,
    progress: project.progress ?? { done: 0, total: 0 },
    nextDue: project.nextDue ?? null,
    unseen: project.unseen ?? { unread: 0, mentions: 0 },
    lastActor: project.lastActor ?? null,
  };
}

/** Delegates to the shared resolver, so the icon and this label always agree. */
function orgNameOf(project: any): string {
  return projectOrgName(project);
}

function activityOf(project: any): number {
  return new Date(project.lastActivityAt || project.updatedAt).getTime();
}

/** "Fri" this week, "12 Mar" beyond it. A weekday is easier to act on. */
function dueLabel(iso: string): string {
  const due = new Date(iso);
  const days = Math.round((due.getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 7) return due.toLocaleDateString(undefined, { weekday: 'short' });
  return due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * WHAT YOU OWE. Open tasks assigned to you, with lateness called out.
 *
 * `waitingOn` sits here too but reads as its own line, because work you handed
 * to somebody else is a different action — chase it, don't do it.
 */
function YoursCell({ s }: { s: Signals }) {
  if (s.mine.open === 0 && s.waitingOn === 0) {
    return <span className="text-sm text-grey-300">&mdash;</span>;
  }
  return (
    <div className="text-sm leading-tight">
      {s.mine.open > 0 ? (
        <div className="font-medium text-secondary">
          {s.mine.open} task{s.mine.open === 1 ? '' : 's'}
        </div>
      ) : (
        <div className="text-grey-400">Nothing yours</div>
      )}
      {s.mine.overdue > 0 && (
        <div className="flex items-center gap-1 font-medium text-error">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {s.mine.overdue} overdue
        </div>
      )}
      {s.mine.overdue === 0 && s.mine.dueSoon > 0 && (
        <div className="text-amber-600">{s.mine.dueSoon} due soon</div>
      )}
      {s.waitingOn > 0 && (
        <div className="text-grey-500" title="Tasks you assigned to someone else that are now late">
          {s.waitingOn} awaiting others
        </div>
      )}
    </div>
  );
}

/**
 * WHAT CHANGED. Unread activity since you last looked, mentions called out.
 *
 * This is the number the old card could never show: "42 messages" is the same
 * for everybody, where "6 new" is a fact about you.
 */
function NewCell({ s }: { s: Signals }) {
  if (s.unseen.unread === 0) {
    return <span className="text-sm text-grey-300">&mdash;</span>;
  }
  return (
    <div className="text-sm leading-tight">
      <div className="font-medium text-accent">{s.unseen.unread} new</div>
      {s.unseen.mentions > 0 && (
        <div className="flex items-center gap-1 font-medium text-primary">
          <AtSign className="h-3 w-3 shrink-0" />
          {s.unseen.mentions} you
        </div>
      )}
    </div>
  );
}

/** How far the project has actually got, plus what lands next. */
function ProgressCell({ s }: { s: Signals }) {
  const { done, total } = s.progress;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="text-sm leading-tight">
      {total > 0 ? (
        <>
          <div
            className="h-1.5 w-24 overflow-hidden rounded-full bg-grey-100"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${done} of ${total} tasks complete`}
          >
            <div
              className={cn('h-full rounded-full', done === total ? 'bg-success' : 'bg-accent')}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1 text-xs text-grey-500">
            {done}/{total}
          </div>
        </>
      ) : (
        <span className="text-grey-300">&mdash;</span>
      )}
      {s.nextDue && (
        <div className="mt-0.5 text-xs text-grey-500" title="Next due date in this project">
          due {dueLabel(s.nextDue)}
        </div>
      )}
    </div>
  );
}

/**
 * Pinning is not an admin power — a favourite is a private bookmark that
 * changes nothing anyone else sees. It also feeds the sidebar shortlist, so
 * this is how a project gets there.
 */
function FavoriteButton({ isFavorited, onToggle }: { isFavorited: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      data-testid="project-favorite"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      aria-pressed={isFavorited}
      aria-label={isFavorited ? 'Unpin this project' : 'Pin this project'}
      title={isFavorited ? 'Unpin' : 'Pin to the top and to your sidebar'}
      className={cn(
        'rounded p-1 transition-all',
        isFavorited
          ? 'text-warning opacity-100'
          : 'text-grey-300 opacity-0 hover:text-warning focus:opacity-100 group-hover:opacity-100',
      )}
    >
      <Star className={cn('h-4 w-4', isFavorited && 'fill-warning')} />
    </button>
  );
}

export function Projects() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // Favorites are for agency people; clients get one plain list (useCanFavorite)
  const canFavorite = useCanFavorite();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<{ id: string; name: string; description?: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Solid admins, and a partner agency's admins (for their agency), start projects
  const canCreateProjects = getCanCreateProjects(user);

  /**
   * Every filter lives in the URL, so a filtered view is a link somebody can
   * send and the back button walks it. `organizationId` keeps its old name —
   * the sidebar shortlist and existing links already point at it.
   */
  const organizationId = searchParams.get('organizationId');
  const searchQuery = searchParams.get('q') ?? '';
  const isInternalOnly = searchParams.get('internal') === 'true';
  const activeFilter: FilterTab = searchParams.get('filter') === 'archived' ? 'archived' : 'active';
  const isArchived = activeFilter === 'archived';
  const sort = (searchParams.get('sort') as SortKey) || 'activity';

  const setParam = (key: string, value: string | null, replace = false) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace });
  };

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', organizationId, isArchived, isInternalOnly],
    queryFn: () => projectsApi.list({
      organizationId: organizationId || undefined,
      archived: isArchived,
      internalOnly: isInternalOnly,
    }),
  });

  // Feeds the organization filter. The server still does the org filtering —
  // owner-vs-client resolution is not something to re-derive on the client.
  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? projectsApi.unfavorite(id) : projectsApi.favorite(id),
    onMutate: async ({ id, isFavorited }) => {
      const key = ['projects', organizationId, isArchived, isInternalOnly];
      await queryClient.cancelQueries({ queryKey: ['projects'] });
      const previousProjects = queryClient.getQueryData<any[]>(key);
      if (previousProjects) {
        queryClient.setQueryData(
          key,
          previousProjects.map((p: any) => (p.id === id ? { ...p, isFavorited: !isFavorited } : p)),
        );
      }
      return { previousProjects };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousProjects) {
        queryClient.setQueryData(
          ['projects', organizationId, isArchived, isInternalOnly],
          context.previousProjects,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.archive(projectId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.unarchive(projectId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });

  const handleArchiveProject = (projectId: string, currentlyArchived: boolean) => {
    (currentlyArchived ? unarchiveMutation : archiveMutation).mutate(projectId);
  };

  const { pinned, rest, matchCount } = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const matches = (projects ?? []).filter(
      (p: any) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        orgNameOf(p).toLowerCase().includes(q),
    );

    const compare = (a: any, b: any): number => {
      switch (sort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'organization':
          return orgNameOf(a).localeCompare(orgNameOf(b)) || a.name.localeCompare(b.name);
        case 'work': {
          const aw = signalsOf(a);
          const bw = signalsOf(b);
          // Late first, then imminent, then merely open — a project with one
          // overdue task outranks a project with six that are not due yet.
          // Mentions break the remaining ties: being named by somebody is a
          // stronger claim on you than an undated task.
          return (
            bw.mine.overdue - aw.mine.overdue ||
            bw.mine.dueSoon - aw.mine.dueSoon ||
            bw.mine.open - aw.mine.open ||
            bw.unseen.mentions - aw.unseen.mentions ||
            activityOf(b) - activityOf(a)
          );
        }
        default:
          return activityOf(b) - activityOf(a);
      }
    };

    const sorted = [...matches].sort(compare);
    return {
      // Pinned respects every filter. A band that ignored the org filter would
      // show projects from a client you deliberately filtered out.
      pinned: canFavorite ? sorted.filter((p: any) => p.isFavorited) : [],
      rest: canFavorite ? sorted.filter((p: any) => !p.isFavorited) : sorted,
      matchCount: matches.length,
    };
  }, [projects, searchQuery, sort, canFavorite]);

  const orgOptions = useMemo(
    () => [
      { value: '', label: 'All organizations' },
      ...(organizations ?? [])
        .slice()
        .sort((a: any, b: any) => a.name.localeCompare(b.name))
        .map((org: any) => ({
          value: org.id,
          label: org._count?.projects ? `${org.name} (${org._count.projects})` : org.name,
        })),
    ],
    [organizations],
  );

  const selectedOrgName = organizations?.find((o: any) => o.id === organizationId)?.name;

  /**
   * ONE row shape. The grid is gone.
   *
   * A card grid scatters the same number to a different x-position on every
   * tile, which defeats the one thing a list is for: running your eye down a
   * column. Cards earn that cost back when the item has a visual identity — a
   * cover, a photo, a logo — and a project has a letter tile. Keeping both also
   * meant keeping two layouts in sync, and they had already drifted: only the
   * card showed the description.
   *
   * Two lines, fixed columns:
   *   line 1   name, then Yours / New / progress / org / when
   *   line 2   description, and the second half of each figure
   */
  const renderRow = (project: any) => {
    const s = signalsOf(project);
    const org = orgNameOf(project);
    return (
      <Card key={project.id} hover className="group" data-testid="project-row">
        <div className="flex items-start gap-4">
          <Link to={`/projects/${project.id}`} className="flex min-w-0 flex-1 items-start gap-3">
            <ProjectIcon project={project} size="md" muted={isArchived} />
            <div className="min-w-0 flex-1">
              <h3 className="flex items-center gap-2 font-semibold text-secondary transition-colors hover:text-primary">
                {project.isPrivate && <Lock className="h-3.5 w-3.5 shrink-0 text-grey-500" />}
                <span className="truncate">{project.name}</span>
                {isArchived && <span className="badge badge-grey shrink-0 text-xs">Archived</span>}
              </h3>
              {/* The organization sits directly under the name because the name
                  often does not identify the row — a dozen projects are called
                  "Support Program", and the client is what tells them apart. It
                  used to live in a right-hand column, far from the thing it
                  qualifies. */}
              <p className="mt-0.5 truncate text-sm">
                {org && <span className="font-medium text-secondary-600">{org}</span>}
                {org && project.description && <span className="text-grey-400"> · </span>}
                {project.description && <span className="text-grey-500">{project.description}</span>}
                {!org && !project.description && (
                  <span className="text-grey-400">No description</span>
                )}
              </p>
            </div>
          </Link>

          {/* Fixed widths, so every figure starts at the same x on every row —
              that alignment IS the feature. */}
          <div className="hidden shrink-0 items-start gap-6 md:flex">
            <div className="w-28">
              <YoursCell s={s} />
            </div>
            <div className="w-20">
              <NewCell s={s} />
            </div>
            <div className="w-24">
              <ProgressCell s={s} />
            </div>
            <div className="w-40 text-sm leading-tight">
              <div className="flex items-center gap-1.5 text-xs text-grey-500">
                {s.lastActor && (
                  <Avatar
                    src={s.lastActor.avatarUrl ?? undefined}
                    name={s.lastActor.name || '?'}
                    size="xs"
                  />
                )}
                <span className="truncate">
                  {s.lastActor?.name ? `${s.lastActor.name.split(' ')[0]} · ` : ''}
                  {formatRelativeTime(project.lastActivityAt || project.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {canFavorite && (
              <FavoriteButton
                isFavorited={!!project.isFavorited}
                onToggle={() => favoriteMutation.mutate({ id: project.id, isFavorited: !!project.isFavorited })}
              />
            )}
            {getIsProjectAdmin(user, project.organizationId) && (
              <AdminActionsMenu
                actions={[
                  createEditAction(() => {
                    setEditingProject(project);
                    setShowEditModal(true);
                  }),
                  createArchiveAction(() => handleArchiveProject(project.id, isArchived), isArchived),
                ]}
              />
            )}
          </div>
        </div>

        {/* Below md the columns cannot hold their alignment, so the figures wrap
            under the name rather than being dropped — a phone still needs to
            know what is late. */}
        <div className="mt-3 flex flex-wrap items-start gap-x-6 gap-y-2 border-t border-grey-100 pt-3 md:hidden">
          <YoursCell s={s} />
          <NewCell s={s} />
          <ProgressCell s={s} />
          {/* The org is already under the name on every width, so this line
              carries only the time. */}
          <div className="text-xs text-grey-500">
            {formatRelativeTime(project.lastActivityAt || project.updatedAt)}
          </div>
        </div>
      </Card>
    );
  };

  const renderGroup = (items: any[]) => <div className="space-y-3">{items.map(renderRow)}</div>;

  return (
    <div className="w-full">
      <PageHeader
        title={isInternalOnly && selectedOrgName ? `${selectedOrgName} Internal Projects` : 'Projects'}
        subtitle="Manage and organize your projects"
        actions={
          canCreateProjects && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          )
        }
      />

      {/* One filter strip. Search stays put when an organization is chosen —
          it used to vanish, so narrowing to a client cost you the ability to
          search within it. */}
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center" data-testid="projects-filters">
        <div className="relative min-w-0 flex-1 lg:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-grey-400" />
          <input
            type="search"
            data-testid="projects-search"
            aria-label="Search projects"
            placeholder="Search projects..."
            value={searchQuery}
            // `replace` so typing a query doesn't bury the back button under
            // one history entry per keystroke.
            onChange={(e) => setParam('q', e.target.value, true)}
            className="input pl-10 pr-9"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setParam('q', null, true)}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-grey-400 hover:bg-grey-100 hover:text-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="w-full lg:w-56">
          <Select
            selectSize="sm"
            aria-label="Filter by organization"
            data-testid="projects-org-filter"
            options={orgOptions}
            value={organizationId ?? ''}
            onChange={(e) => setParam('organizationId', e.target.value || null)}
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg bg-grey-50 p-1">
          {([
            { key: 'active', label: 'Active', icon: FolderKanban },
            { key: 'archived', label: 'Archived', icon: Archive },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setParam('filter', tab.key === 'active' ? null : tab.key)}
              aria-pressed={activeFilter === tab.key}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                activeFilter === tab.key
                  ? 'bg-white text-secondary shadow-sm'
                  : 'text-grey-500 hover:text-secondary',
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <div className="w-44">
            <Select
              selectSize="sm"
              aria-label="Sort projects"
              data-testid="projects-sort"
              options={SORTS}
              value={sort}
              onChange={(e) => setParam('sort', e.target.value === 'activity' ? null : e.target.value)}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : matchCount === 0 ? (
        <EmptyState
          icon={isArchived ? Archive : FolderKanban}
          title={
            isArchived
              ? 'No archived projects'
              : searchQuery || organizationId
                ? 'No projects match these filters'
                : 'No projects found'
          }
          description={
            isArchived
              ? 'Nothing has been archived here yet.'
              : searchQuery || organizationId
                ? 'Try a different search, or choose All organizations.'
                : 'Create your first project to get started collaborating with your team'
          }
          action={
            searchQuery || organizationId ? (
              <Button
                variant="ghost"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('q');
                  next.delete('organizationId');
                  setSearchParams(next);
                }}
              >
                Clear filters
              </Button>
            ) : (
              !isArchived && canCreateProjects && (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4" />
                  Create Project
                </Button>
              )
            )
          }
        />
      ) : (
        <div className="space-y-8">
          {pinned.length > 0 && (
            <section aria-labelledby="pinned-heading" data-testid="projects-pinned">
              <h2
                id="pinned-heading"
                className="mb-3 flex items-center gap-2 text-caption font-bold uppercase tracking-wider text-grey-400"
              >
                <Star className="h-3.5 w-3.5 fill-warning text-warning" />
                Pinned
              </h2>
              {renderGroup(pinned)}
            </section>
          )}

          <section aria-labelledby="all-projects-heading" data-testid="projects-list">
            {pinned.length > 0 && (
              <h2
                id="all-projects-heading"
                className="mb-3 text-caption font-bold uppercase tracking-wider text-grey-400"
              >
                {selectedOrgName ? selectedOrgName : 'All projects'}
              </h2>
            )}
            {rest.length > 0 ? (
              renderGroup(rest)
            ) : (
              <p className="text-sm text-grey-500">Everything here is pinned.</p>
            )}
          </section>
        </div>
      )}

      {/* The organizations screen keeps its route — logos, member counts and
          the deep link into the accounts portal live there — it just no longer
          spends a slot in the left nav. */}
      <div className="mt-8 border-t border-grey-100 pt-4">
        <Link
          to="/organizations"
          className="inline-flex items-center gap-2 text-sm text-grey-500 transition-colors hover:text-secondary"
        >
          <Settings2 className="h-4 w-4" />
          Manage organizations
        </Link>
      </div>

      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={(project) => {
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          setShowCreateModal(false);
          navigate(`/projects/${project.id}`);
        }}
        organizationId={organizationId || undefined}
        organizationName={selectedOrgName}
        isInternal={isInternalOnly}
      />

      <EditProjectModal
        isOpen={showEditModal}
        project={editingProject}
        onClose={() => {
          setShowEditModal(false);
          setEditingProject(null);
        }}
      />
    </div>
  );
}
